<template>
    <div class="relative" v-click-outside="() => (showCardsList = false)">
        <label class="relative">
            <input
                class="h-10 w-full flex items-center gap-2 text-left pl-3 text-slate-500 relative bg-white rounded border border-slate-200 py-1.5 placeholder:text-slate-500 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                v-model="cardQuery"
                @focus="() => (showCardsList = true)"
                ref="creditCardInput"
                :placeholder="modelValue ? '' : 'Search for a card...'"
                :disabled="disabled"
            />
            <span
                class="w-[90%] flex items-center gap-2 absolute pr-14 left-2 top-1/2 -translate-y-1/2 whitespace-nowrap truncate pointer-events-none text-slate-500 text-sm"
                v-if="modelValue && !cardQuery"
            >
                <template v-if="modelValue">
                    <ImageLoader>
                        <template #image>
                            <img
                                :src="cdnUrl + modelValue.image_url"
                                :alt="modelValue.title"
                                class="w-8 h-5 rounded-sm"
                            />
                        </template>
                        <template #error>
                            <Icon
                                icon="credit-card"
                                class="text-slate-600 w-8 h-5"
                            />
                        </template>
                    </ImageLoader>
                    {{ modelValue.title }}
                    {{
                        modelValue.last_four ? `(${modelValue.last_four})` : ''
                    }}
                </template>
            </span>
            <button
                v-if="cardQuery || modelValue"
                class="block leading-none text-red-600 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                @click="clearCard"
            >
                <Icon icon="close" />
            </button>
        </label>

        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white rounded-lg shadow-sm border border-slate-200 absolute z-[99999]"
                    v-show="showCardsList"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                >
                    <template
                        v-for="(cards, issuer) in groupedCards"
                        :key="issuer"
                    >
                        <p class="text-slate-500 text-sm mb-1 pt-1">
                            {{ issuer }}
                        </p>
                        <div class="mb-2 border-b border-slate-200 pb-2">
                            <label
                                v-for="card in cards"
                                :key="card.id"
                                class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 text-sm bg-white rounded hover:bg-slate-100 transition-colors mb-1 cursor-pointer"
                                @click="selectCard(card)"
                            >
                                <ImageLoader>
                                    <template #image>
                                        <img
                                            :src="cdnUrl + card.image_url"
                                            :alt="card.title"
                                            class="w-8 h-5 rounded-sm"
                                        />
                                    </template>
                                    <template #error>
                                        <Icon
                                            icon="credit-card"
                                            class="text-slate-600 w-8 h-5"
                                        />
                                    </template>
                                </ImageLoader>
                                {{ card.title }}
                                {{
                                    card.last_four ? `(${card.last_four})` : ''
                                }}
                            </label>
                        </div>
                    </template>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const props = defineProps({
    modelValue: {
        type: Object,
        default: null,
    },
    institutionCode: {
        type: String,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const cdnUrl = window.cdnUrl;
const cardQuery = ref('');
const showCardsList = ref(false);
const creditCardInput = ref(null);
const width = ref(0);
const top = ref(0);
const left = ref(0);
const groupedCards = ref({});

defineOptions({
    directives: {
        'click-outside': {
            mounted(el, binding) {
                el.clickOutsideEvent = (event) => {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener('click', el.clickOutsideEvent);
            },
            unmounted(el) {
                document.removeEventListener('click', el.clickOutsideEvent);
            },
        },
    },
});

const loadCards = async () => {
    try {
        const response = await axios.get('/api/admin/cards/search-grouped', {
            params: { s: cardQuery.value },
        });
        groupedCards.value = response.data;
    } catch (error) {
        console.error('Error loading cards:', error);
    }
};

const setCardsDropdownPosition = () => {
    if (!creditCardInput.value) return;
    const rect = creditCardInput.value.getBoundingClientRect();
    width.value = rect.width;
    top.value = rect.bottom + window.scrollY + 4;
    left.value = rect.left + window.scrollX;
};

const selectCard = (card) => {
    emit('update:modelValue', card);
    showCardsList.value = false;
    cardQuery.value = '';
    if (creditCardInput.value) {
        creditCardInput.value.blur();
    }
};

const clearCard = () => {
    emit('update:modelValue', null);
    cardQuery.value = '';
    showCardsList.value = false;
};

watch(cardQuery, () => {
    loadCards();
});

watch(showCardsList, (val) => {
    if (val) {
        setCardsDropdownPosition();
        loadCards();
    }
});

onMounted(() => {
    window.addEventListener('resize', setCardsDropdownPosition);
    window.addEventListener('scroll', setCardsDropdownPosition);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setCardsDropdownPosition);
    window.removeEventListener('scroll', setCardsDropdownPosition);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
